import PropTypes from "prop-types";
import React from "react";
import { intlShape, injectIntl, FormattedMessage } from "react-intl";
import { connect } from "react-redux";
import { formValueSelector } from "redux-form";
import {
  loadBenefitPlans,
  loadClaimsHistory,
  fetchHistoricalClaimsReport,
  loadClaimItems,
  getEmployeesByDelegation,
} from "actions/claimActions";
import {
  getDefaultBenefitPlan,
  getAllBenefitPlans,
  getClaimListByType,
  claimCategories,
  groupItemsByCategory,
  getAllEmployeeByDelegation,
  getDefaultDelegateEmployeeId,
} from "reducers/claimsReducer";
import { Link } from "react-router-dom";
import { Card, CardSection, Toolbar, ToolbarSection, Button, Loader } from "cxa-components";
import { FileDownloadIcon } from "cxa-icons";
import AccountsList from "components/claim/AccountsList";
import messages from "components/messagesDescription";
import { PlanYearSelectField, DelegatorSelectField } from "./widgets";
import ClaimItemSelectionForm from "./ClaimItemSelectionForm";
import { getShortLivedToken } from "actions/loginActions";
import { showEstimatedDateMessage } from "./util";

const benefitPeriodKey = "cxa_benefitPeriod";
const formName = "claimHistoryForm";

const formatters = {
  //expecting field ID here
  Id: ({ objectId, id, ...row } = {}, column, intl, selectedDelegator, getDonateCategory) => ({
    className: "c-action",
    html:
      selectedDelegator && selectedDelegator.toLowerCase() !== "myself" ? (
        <Link
          to={`/${
            getDonateCategory ? "donations" : "claims"
          }/view/${objectId}/${selectedDelegator}`}
        >
          {id}
        </Link>
      ) : (
        <Link to={`/${getDonateCategory ? "donations" : "claims"}/view/${objectId}`}>{id}</Link>
      ),
  }),
};

const ClaimTypeList = ({
  claimCategories,
  claims,
  pageFields,
  selectedDelegator,
  getDonateCategory,
  intl,
}) =>
  claimCategories.map(
    category =>
      claims[category.id] &&
      claims[category.id].length > 0 && (
        <div key={category.id}>
          <h3 className="h4">
            <FormattedMessage
              id={getDonateCategory ? "Donations" : category.label}
              defaultMessage={getDonateCategory ? "Donations" : category.label}
            />
          </h3>
          <AccountsList
            id={category.id}
            rows={claims[category.id]}
            columns={pageFields}
            formatters={formatters}
            selectedDelegator={selectedDelegator}
            getDonateCategory={getDonateCategory}
          />
          {showEstimatedDateMessage(pageFields) && (
            <div
              className="apl-claims__payment-description"
              dangerouslySetInnerHTML={{
                __html: intl.formatMessage(messages.claims.historyClaimsEstimatedPaymentDate),
              }}
            />
          )}
        </div>
      )
  );

class ClaimsHistoryPage extends React.Component {
  state = {
    isFetching: true,
  };

  componentDidMount() {
    const {
      loadBenefitPlans,
      loadClaimItems,
      benefitPeriod,
      isServiceProviderClaimItem,
      loadClaimsHistory,
      getEmployeesByDelegation,
      delegateToEmployeeId,
      getDonateCategory,
    } = this.props;

    getEmployeesByDelegation().then(() => {
      loadBenefitPlans();

      if (benefitPeriod) {
        loadClaimsHistory(benefitPeriod, delegateToEmployeeId, getDonateCategory);
        loadClaimItems(
          benefitPeriod,
          isServiceProviderClaimItem,
          delegateToEmployeeId,
          getDonateCategory
        );
      }
    });
  }

  // eslint-disable-next-line react/no-deprecated
  componentWillReceiveProps(nextProps) {
    if (!nextProps.isFetching) {
      this.setState({
        isFetching: false,
      });
    }
  }

  componentDidUpdate(prevProps) {
    const {
      benefitPeriod,
      loadClaimsHistory,
      loadClaimItems,
      isServiceProviderClaimItem,
      delegateToEmployeeId = null,
      getDonateCategory,
    } = this.props;

    const isClaimOrDelegationChange =
      prevProps.benefitPeriod !== benefitPeriod ||
      prevProps.delegateToEmployeeId !== delegateToEmployeeId;

    if (isClaimOrDelegationChange) {
      loadClaimsHistory(benefitPeriod, delegateToEmployeeId, getDonateCategory);
      loadClaimItems(
        benefitPeriod,
        isServiceProviderClaimItem,
        delegateToEmployeeId,
        getDonateCategory
      );
    }
  }

  componentWillUnmount() {
    const { benefitPeriod } = this.props;

    localStorage.setItem(benefitPeriodKey, benefitPeriod);
  }

  render() {
    const {
      benefitPeriod,
      benefitPlans,
      claims,
      fetchHistoricalClaimsReport,
      downloadHistoricalClaimsUrl,
      pageFields,
      hasClaims,
      intl,
      isFetching,
      claimItems = [],
      onSubmitSelection,
      isEmployeesByDelegationEnabled,
      employeesByDelegation,
      delegateToEmployeeId,
      getDonateCategory,
      ...props
    } = this.props;

    if (this.state.isFetching) {
      return <Loader />;
    }

    return (
      <div>
        <h2>
          {intl.formatMessage(
            getDonateCategory
              ? messages.donationHistoryDescription
              : messages.claimsHistoryDescription
          )}
        </h2>
        <Card className="card">
          <CardSection>
            {isEmployeesByDelegationEnabled && (
              <Toolbar>
                <ToolbarSection>
                  <DelegatorSelectField
                    formName={formName}
                    employeesByDelegation={employeesByDelegation}
                  />
                </ToolbarSection>
              </Toolbar>
            )}
            <Toolbar>
              <ToolbarSection>
                <PlanYearSelectField benefitPlans={benefitPlans} formName={formName} {...props} />
              </ToolbarSection>
            </Toolbar>
            {hasClaims && (
              <ClaimTypeList
                claims={claims}
                claimCategories={claimCategories}
                pageFields={pageFields}
                selectedDelegator={delegateToEmployeeId}
                getDonateCategory={getDonateCategory}
                intl={intl}
              />
            )}
            {!hasClaims && !isFetching && claimItems.length === 0 && (
              <p>{intl.formatMessage(messages.noClaimsDescription)}</p>
            )}
          </CardSection>
          {downloadHistoricalClaimsUrl && hasClaims && (
            <Toolbar>
              <ToolbarSection>
                <Button
                  onClick={() => fetchHistoricalClaimsReport(downloadHistoricalClaimsUrl)}
                  secondary
                >
                  <FileDownloadIcon />
                  {intl.formatMessage(messages.exportAllDescription)}
                </Button>
              </ToolbarSection>
            </Toolbar>
          )}
        </Card>
        {isFetching && <Loader />}
        {!isFetching && claimItems.length > 0 && (
          <Card>
            <CardSection>
              <h5>{intl.formatMessage(messages.claims.claimSubmittedStatusDescription)}.</h5>
              <div className="field__label">
                {intl.formatMessage(messages.claims.claimSubmittedStatusNote)}.
              </div>
              <ClaimItemSelectionForm
                onSubmit={onSubmitSelection}
                benefitPlans={benefitPlans}
                functionReferenceId="ClaimList"
                selectedDelegator={delegateToEmployeeId}
                {...this.props}
              />
            </CardSection>
          </Card>
        )}
      </div>
    );
  }
}

ClaimsHistoryPage.propTypes = {
  benefitPeriod: PropTypes.string,
  benefitPlans: PropTypes.array,
  claims: PropTypes.object,
  hasClaims: PropTypes.bool,
  loadBenefitPlans: PropTypes.func.isRequired,
  loadClaimsHistory: PropTypes.func.isRequired,
  fetchHistoricalClaimsReport: PropTypes.func.isRequired,
  pageFields: PropTypes.array,
  intl: intlShape.isRequired,
  claimCategories: PropTypes.array,
  isFetching: PropTypes.bool,
  downloadHistoricalClaimsUrl: PropTypes.string,
  loadClaimItems: PropTypes.func,
  isServiceProviderClaimItem: PropTypes.bool,
  claimItems: PropTypes.array,
  onSubmitSelection: PropTypes.func,
  getEmployeesByDelegation: PropTypes.func,
  isEmployeesByDelegationEnabled: PropTypes.bool,
  delegateToEmployeeId: PropTypes.string,
  employeesByDelegation: PropTypes.array,
  getDonateCategory: PropTypes.bool,
};

const mapStateToProps = (state, { push }) => {
  const {
    claims: {
      claimList,
      benefitPlans,
      downloadHistoricalClaimsUrl,
      claimItems,
      claimItemGroups,
      claimItemCategories,
      employeesByDelegation: employeesDelegation,
    },
  } = state;

  const savedBenefitPeriod = localStorage.getItem(benefitPeriodKey);
  const selector = formValueSelector(formName);
  const defaultPlanId = getDefaultBenefitPlan(benefitPlans) || null;
  const benefitPeriod = selector(state, "benefitPeriod") || savedBenefitPeriod || defaultPlanId;
  const claims = getClaimListByType(claimList, benefitPeriod);
  const hasClaims = claimCategories.some(
    category => claims && claims[category.id] && claims[category.id].length > 0
  );
  const employeesByDelegation = getAllEmployeeByDelegation(employeesDelegation);
  const isEmployeesByDelegationEnabled = employeesByDelegation.length > 0;
  const defaultDelegateEmployeeId = getDefaultDelegateEmployeeId(employeesDelegation) || null;
  const delegateToEmployeeId =
    selector(state, "employeesByDelegation") || defaultDelegateEmployeeId;

  return {
    benefitPeriod,
    benefitPlans: getAllBenefitPlans(benefitPlans),
    claims,
    pageFields: claimList.hisPageFields,
    claimCategories,
    hasClaims,
    initialValues: {
      benefitPeriod: savedBenefitPeriod || defaultPlanId,
    },
    isFetching: claimItems.isFetching,
    isCPFEnabled: claimItems.isCPFEnabled,
    downloadHistoricalClaimsUrl,
    claimItems: groupItemsByCategory(claimItemGroups, claimItemCategories, claimItems),
    isServiceProviderClaimItem: true,
    onSubmitSelection: ({ benefitPeriod, claimTypeId }) =>
      push(`/claims/submit/${claimTypeId}/${benefitPeriod}`),
    isEmployeesByDelegationEnabled,
    delegateToEmployeeId,
    employeesByDelegation,
  };
};

const mapDispatchToProps = {
  loadBenefitPlans,
  loadClaimsHistory,
  fetchHistoricalClaimsReport,
  loadClaimItems,
  getShortLivedToken,
  getEmployeesByDelegation,
};

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(ClaimsHistoryPage));



// WEBPACK FOOTER //
// ./components/claim/ClaimsHistoryPage.js